import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import memoizeOne, { type MemoizedFn } from "memoize-one"

import type { TTFunction } from "$/i18n/i18n"

import type { TModuleExcerptModuleUiModel } from "@/3514/components"
import { EModulePhaseProgressStatus, EModulePhaseTrackStatus } from "@/3514/types"

import {
    EJourneyAchievementType,
    type IJourneyAchievementModel,
    type IModuleExcerptModel,
    type IModuleExcerptPhaseModel
} from "../types"

import { participantHeaderUiConfig } from "./participant.header.ui.config"
import type { TJourneyAchievementModelToRender, TModuleExcerptModelToRender } from "./participant.header.ui.types"

const {
    journeyAchievementBadgeTextsMap,
    journeyAchievementIconsMap,
    journeyAchievementBadgeVariantsMap,
    journeyAchievementsLabelTranslationsMap
} = participantHeaderUiConfig

const adaptJourneyAchievementsToRender: MemoizedFn<
    (models: IJourneyAchievementModel[], t: TTFunction) => TJourneyAchievementModelToRender[]
> = memoizeOne(function (models: IJourneyAchievementModel[], t: TTFunction): TJourneyAchievementModelToRender[] {
    const _sortOrder: EJourneyAchievementType[] = [
        EJourneyAchievementType.SessionAttendance,
        EJourneyAchievementType.CurrentStreak,
        EJourneyAchievementType.LongestStreak,
        EJourneyAchievementType.StarsEarned
    ]

    return [...models]
        .sort(
            (a: IJourneyAchievementModel, b: IJourneyAchievementModel): number =>
                _sortOrder.indexOf(a?.type) - _sortOrder.indexOf(b?.type)
        )
        .map(({ type, value }: IJourneyAchievementModel): TJourneyAchievementModelToRender => {
            const {
                accentText: badgeAccentText,
                optionalText: badgeOptionalText
            }: { accentText: string; optionalText?: string } = journeyAchievementBadgeTextsMap[type](value, t)

            return {
                label: t(journeyAchievementsLabelTranslationsMap[type]),
                badgeVariant: journeyAchievementBadgeVariantsMap[type],
                icon: journeyAchievementIconsMap[type],
                badgeAccentText,
                badgeOptionalText
            }
        })
})

function getModuleTitleFromNameAndRank(name: string, rank: number): string {
    return `${rank}: ${name}`
}

function _getUnassignedModuleExcerptStub(rank: number): TModuleExcerptModelToRender {
    return {
        title: { en: "Unassigned", es: "No asignado", pt: "Não atribuído" },
        rank,
        id: 0,
        phases: Array.from(
            { length: 4 },
            (_: unknown, idx: number): IModuleExcerptPhaseModel => ({
                order: idx + 1,
                progressStatus: EModulePhaseProgressStatus.NotAvailable,
                trackStatus: EModulePhaseTrackStatus.NotStarted
            })
        ),
        associatedSession: null,
        isStub: true
    }
}

const getModulesExcerptsToRender: MemoizedFn<
    (models: IModuleExcerptModel[], length: number) => TModuleExcerptModelToRender[]
> = memoizeOne(function (modules: IModuleExcerptModel[], modulesLength: number): TModuleExcerptModelToRender[] {
    const result: TModuleExcerptModelToRender[] = []

    let moduleIndex: number = 0

    for (let rank: number = 1; rank <= modulesLength; rank++) {
        if (moduleIndex < modules.length && modules[moduleIndex].rank === rank) {
            result.push({ ...modules[moduleIndex], isStub: false })
            moduleIndex++
        } else {
            result.push(_getUnassignedModuleExcerptStub(rank))
        }
    }

    return result
})

function mapHeaderModuleExcerptModelsToUiModels(
    incomingModels: TModuleExcerptModelToRender[]
): TModuleExcerptModuleUiModel[] {
    return isEmpty(incomingModels)
        ? []
        : map(
              incomingModels,
              (model: TModuleExcerptModelToRender): TModuleExcerptModuleUiModel => ({
                  name: model.title,
                  id: model.id,
                  isStub: model.isStub,
                  rank: model.rank,
                  session: model.associatedSession,
                  phases: model.phases
              })
          )
}

type TUtils = {
    adaptJourneyAchievementsToRender(
        models: IJourneyAchievementModel[],
        t: TTFunction
    ): TJourneyAchievementModelToRender[]
    getModuleTitleFromNameAndRank(name: string, rank: number): string
    getModulesExcerptsToRender(
        modules: TModuleExcerptModelToRender[],
        modulesLength: number
    ): TModuleExcerptModelToRender[]
    mapHeaderModuleExcerptModelsToUiModels(model: TModuleExcerptModelToRender[]): TModuleExcerptModuleUiModel[]
}

const utils: TUtils = {
    adaptJourneyAchievementsToRender,
    getModuleTitleFromNameAndRank,
    getModulesExcerptsToRender,
    mapHeaderModuleExcerptModelsToUiModels
}

export { utils as participantHeaderUiUtils }
